import React from 'react';
import { FiX, FiAlertCircle, FiArrowRight, FiLogIn } from 'react-icons/fi';

/**
 * Professional Login Alert Modal
 * Displays a styled modal asking users to login before accessing worker pages
 */
const LoginAlertModal = ({ isOpen, onClose, onLoginClick }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal Container */}
      <div className="relative w-full max-w-md animate-scale-in">
        {/* Card */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-gold-400 to-cherry-500 px-6 py-8 relative overflow-hidden">
            {/* Decorative circle */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
            
            <div className="relative flex items-center gap-3 mb-2">
              <div className="p-3 bg-white/20 rounded-full">
                <FiAlertCircle className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Authentication Required</h2>
            </div>
            <p className="text-white/90 text-sm ml-12">Please login to continue</p>
          </div>

          {/* Content */}
          <div className="px-6 py-8">
            {/* Message */}
            <div className="mb-6">
              <p className="text-gray-700 text-center leading-relaxed">
                To become a professional service provider on <span className="font-bold text-gold-600">NammaEventz</span>, you need to be logged in first.
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
              <div className="flex gap-3">
                <div className="flex-shrink-0 pt-1">
                  <FiLogIn className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Ready to join?</p>
                  <p className="text-sm text-blue-800">
                    Login with your account and unlock professional opportunities.
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={onLoginClick}
                className="w-full bg-gradient-gold-cherry text-white font-bold py-3 px-4 rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 text-base"
                aria-label="Proceed to login"
              >
                <FiLogIn className="w-5 h-5" />
                <span>Proceed to Login</span>
                <FiArrowRight className="w-5 h-5" />
              </button>

              <button
                onClick={onClose}
                className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-200 transition-all duration-300"
                aria-label="Close dialog"
              >
                Maybe Later
              </button>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-all duration-300 z-10"
            aria-label="Close modal"
          >
            <FiX className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LoginAlertModal;
